import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import '../styles/Signin.css';
import { AuthService } from '../services/auth.service';
import modalNotification from '../utils/notification';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../redux/slices/auth.slice';

const Signin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });

    if (!validateEmail(formData.email) || formData.password.length < 6) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await AuthService.login(formData)
      if (!response?.success) {
        throw new Error(response?.message || 'Invalid credentials. Please try again.');
      }

      modalNotification({
        type: "success",
        message: "Login successful"
      })
      dispatch(setCredentials(response?.data))
      navigate('/dashboard');
    } catch (err) {
      modalNotification({
        type: "error",
        message: err.message || 'Something went wrong. Please try again.'
      })
    } finally {
      setIsLoading(false);
    }
  };

  const emailError = touched.email && !validateEmail(formData.email)
    ? 'Please enter a valid email address.'
    : '';
  const passwordError = touched.password && formData.password.length < 6
    ? 'Password must be at least 6 characters.'
    : '';

  return (
    <div className="signin-page">
      {/* Animated Background */}
      <div className="signin-bg">
        <div className="bg-orb orb-1" />
        <div className="bg-orb orb-2" />
        <div className="bg-orb orb-3" />
        <div className="bg-grid" />
      </div>

      {/* Left Panel */}
      <div className="signin-left">
        <div className="signin-left-content">
          <div className="brand-badge">
            <ShieldCheck size={18} />
            <span>Secure Admin Portal</span>
          </div>
          <h1 className="signin-headline">
            Manage Your<br />
            <span className="headline-accent">Cricket Federation</span><br />
            With Confidence
          </h1>
          <p className="signin-subtext">
            The ITCSF Admin Panel gives you full control over players,
            tournaments, committee members, and federation activities.
          </p>
          {/* <div className="signin-stats">
            <div className="signin-stat">
              <span className="signin-stat-value">07</span>
              <span className="signin-stat-label">Committee Members</span>
            </div>
            <div className="signin-stat-divider" />
            <div className="signin-stat">
              <span className="signin-stat-value">02</span>
              <span className="signin-stat-label">Tournaments</span>
            </div>
            <div className="signin-stat-divider" />
            <div className="signin-stat">
              <span className="signin-stat-value">05</span>
              <span className="signin-stat-label">Inquiries</span>
            </div>
          </div> */}
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="signin-right">
        <div className="signin-card">
          {/* Logo */}
          <div className="signin-logo-wrap">
            <img src="/img/ITCSF-logo.png" alt="ITCSF Logo" className="signin-logo" />
          </div>

          <div className="signin-card-header">
            <h2 className="signin-title">Welcome back</h2>
            <p className="signin-desc">Sign in to your admin account to continue.</p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="signin-error-banner" role="alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="signin-form" noValidate>
            {/* Email Field */}
            <div className={`signin-field ${emailError ? 'field-error' : touched.email && formData.email ? 'field-success' : ''}`}>
              <label htmlFor="signin-email" className="signin-label">
                Email Address
              </label>
              <div className="signin-input-wrap">
                <Mail size={18} className="signin-input-icon" />
                <input
                  id="signin-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="admin@itcsf.org"
                  className="signin-input"
                  autoComplete="email"
                  required
                />
              </div>
              {emailError && (
                <span className="signin-field-error">{emailError}</span>
              )}
            </div>

            {/* Password Field */}
            <div className={`signin-field ${passwordError ? 'field-error' : touched.password && formData.password ? 'field-success' : ''}`}>
              <label htmlFor="signin-password" className="signin-label">
                Password
              </label>
              <div className="signin-input-wrap">
                <Lock size={18} className="signin-input-icon" />
                <input
                  id="signin-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your password"
                  className="signin-input"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="signin-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordError && (
                <span className="signin-field-error">{passwordError}</span>
              )}
            </div>

            {/* Forgot Password */}
            {/* <div className="signin-forgot-row">
              <a href="/forgot-password" className="signin-forgot-link">
                Forgot password?
              </a>
            </div> */}

            {/* Submit Button */}
            <button
              id="signin-submit-btn"
              type="submit"
              className="signin-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={20} className="spinner-icon" />
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign In to Dashboard</span>
              )}
            </button>
          </form>

          <p className="signin-footer-text">
            Access restricted to authorized ITCSF administrators only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signin;
